import {enumContainer} from '@wallaby-ui/core';
import * as React from 'react';
import {useMemo} from 'react';

import {PhDrugRelatedProblemDto} from './PhRecommendationDrpBaseFormatter';
import {PhRecommendationDrpListFormatter} from './PhRecommendationDrpFormatters';
import {PhRecommendationDrpItemFormatter} from './PhRecommendationDrpItemFormatter';
import {useFormatters} from '../light';
import {MerieSchema} from 'src/js/common/types';
import {useAppSelector} from 'src/js/store_hooks';

type DrpCause = MerieSchema.components['schemas']['DrpCause'];
type DrpCauseGroup = Extract<
  DrpCause,
  | 'GROUP_DRUG_FORM'
  | 'GROUP_DRUG_SELECTION'
  | 'GROUP_DOSE_SELECTION'
  | 'GROUP_TREATMENT_DURATION'
  | 'GROUP_DRUG_USE_PROCESS'
  | 'GROUP_OTHER'
>;
type DrpCauseGroupColors = Record<DrpCauseGroup, {background: string}>;
const DRP_CAUSE_GROUP_COLORS: DrpCauseGroupColors = {
  GROUP_DRUG_FORM: {background: '#F6F2FF'},
  GROUP_DRUG_SELECTION: {background: '#FFF0F7'},
  GROUP_DOSE_SELECTION: {background: '#EDF5FF'},
  GROUP_TREATMENT_DURATION: {background: '#E5F6FF'},
  GROUP_DRUG_USE_PROCESS: {background: '#D9FBFB'},
  GROUP_OTHER: {background: '#F2F4F8'},
};

export type UseDrpCauseBackgroundReturnValue = (cause: DrpCause) => string;

export const useDrpCauseBackground = (): UseDrpCauseBackgroundReturnValue => {
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const drpCauseToGroupMap = useMemo(() => {
    const map = {} as Record<string, DrpCauseGroup>;
    enumMap?.DrpCause?.forEach((cause) => {
      map[cause.name] = (cause.parentName ?? cause.name) as DrpCauseGroup;
    });
    return map;
  }, [enumMap?.DrpCause]);

  return (cause: DrpCause): string => {
    const group = drpCauseToGroupMap[cause];
    return DRP_CAUSE_GROUP_COLORS[group]?.background ?? 'var(--dlThemeGray-50)';
  };
};

type Props = {
  problems: PhDrugRelatedProblemDto[];
  dividerWidthOffset: number;
};
export const PhRecommendationDrpCausesFormatter: React.FC<Props> = ({problems, dividerWidthOffset}) => {
  const {tEnum} = useFormatters();
  const getDrpCauseBackground = useDrpCauseBackground();

  return (
    <PhRecommendationDrpListFormatter
      dividerWidthOffset={dividerWidthOffset}
      problems={problems}
      getItems={(drp) => drp.causes}
      renderItem={(cause: DrpCause, index) => (
        <PhRecommendationDrpItemFormatter key={index} value={tEnum('DrpCause', cause)} background={getDrpCauseBackground(cause)} />
      )}
    />
  );
};

PhRecommendationDrpCausesFormatter.displayName = 'PharmaRecommendationDrpCausesFormatter';
